// Monnaie du compte (spec § 3, « Monnaie du compte ») : euro ou dollar, choisie par chacun dans le menu de son avatar.
// Elle commande l'affichage des montants (« ≈ … », cours de la BCE), les prix d'« Offre et couverture » et la
// monnaie demandée à la souscription. Valeur par défaut, fixée une fois à la première connexion : d'après les
// territoires du partenaire, sinon d'après la région du navigateur. Jamais d'après l'adresse IP.

export type AccountCurrency = "EUR" | "USD";
export const ACCOUNT_CURRENCIES: AccountCurrency[] = ["EUR", "USD"];

/** Les 27 pays de l'Union européenne (codes ISO). */
const EU = new Set(
  "AT BE BG CY CZ DE DK EE ES FI FR GR HR HU IE IT LT LU LV MT NL PL PT RO SE SI SK".split(
    " ",
  ),
);

/** Langues parlées presque seulement en Europe : sans région, elles valent l'euro. L'anglais, l'espagnol ou le
 * portugais, parlés surtout ailleurs, valent le dollar. */
const EUROPEAN_LANGUAGES = new Set(
  "bg cs da de el et fi fr ga hr hu it lb lt lv mt nl pl ro sk sl sv".split(" "),
);

type Territory = { country?: string | null; zone?: string | null };
type Zone = { code: string; countries: string[] };

/** Pays couverts par des territoires : pays cités et pays de leurs zones. */
export const territoryCountries = (territories: Territory[], zones: Zone[]) => [
  ...new Set(
    territories.flatMap((territory) =>
      territory.zone
        ? (zones.find((zone) => zone.code === territory.zone)?.countries ?? [])
        : territory.country
          ? [territory.country]
          : [],
    ),
  ),
];

/** Monnaie d'après les langues du navigateur (`navigator.languages`) : la première qui précise une région décide. */
export const currencyFromBrowser = (
  languages: readonly string[],
): AccountCurrency => {
  for (const tag of languages) {
    const region = /^[a-z]{2,3}[-_](?:[A-Za-z]{4}[-_])?([A-Za-z]{2})\b/i.exec(
      tag,
    )?.[1];
    if (region) return EU.has(region.toUpperCase()) ? "EUR" : "USD";
  }
  const language = languages[0]?.slice(0, 2).toLowerCase();
  return language && EUROPEAN_LANGUAGES.has(language) ? "EUR" : "USD";
};

/** Monnaie proposée à la première connexion. */
export const defaultCurrency = ({
  countries,
  world = false,
  browser,
}: {
  countries: string[];
  world?: boolean;
  browser: readonly string[];
}): AccountCurrency => {
  if (world) return "USD";
  if (countries.length)
    return countries.every((country) => EU.has(country)) ? "EUR" : "USD";
  return currencyFromBrowser(browser);
};

/** Montant en euros converti dans la monnaie du compte ; null sans montant ou sans cours. */
export const fromEur = (
  value: number | null | undefined,
  currency: AccountCurrency,
  rates: Record<string, number>,
) => {
  if (value == null) return null;
  if (currency === "EUR") return value;
  const rate = rates[currency];
  return rate ? value * rate : null;
};

/** Une estimation « ≈ » n'a de sens que si la source n'est pas déjà dans la monnaie du compte. */
export const showsEstimate = (
  project: { budget_currency?: string | null; budget_eur?: number | null },
  currency: AccountCurrency,
) => project.budget_eur != null && project.budget_currency !== currency;

/** Montant dans une monnaie, au format court (« 3,1 M€ », « $3.1M ») ou entier (« 849 $ »). */
export const formatMoney = (
  value: number,
  currency: AccountCurrency,
  locale: string,
  compact = true,
) =>
  new Intl.NumberFormat(locale, {
    style: "currency",
    currency,
    currencyDisplay: "narrowSymbol",
    notation: compact ? "compact" : "standard",
    maximumFractionDigits: compact ? 1 : 0,
  }).format(value);

/** Montant en euros affiché comme une estimation dans la monnaie du compte (« ≈ 3,5 M$ ») ; vide sans cours. */
export const formatEstimate = (
  valueEur: number | null | undefined,
  currency: AccountCurrency,
  rates: Record<string, number>,
  locale: string,
) => {
  const value = fromEur(valueEur, currency, rates);
  return value == null ? "" : `≈ ${formatMoney(value, currency, locale)}`;
};
